import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

/**
 * Reveals its children once, when they first scroll into view.
 *
 * Deliberately restrained: a short rise and fade, staggered by `delay`. The
 * page should feel unhurried, so nothing bounces or slides sideways. Honours
 * prefers-reduced-motion by rendering the final state immediately.
 */
const Reveal = ({ children, delay = 0, y = 24, sx }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setShown(true); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShown(true); io.disconnect(); } },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : `translate3d(0, ${y}px, 0)`,
        transition: `opacity 800ms cubic-bezier(.22,.61,.36,1) ${delay}ms, transform 800ms cubic-bezier(.22,.61,.36,1) ${delay}ms`,
        willChange: shown ? 'auto' : 'opacity, transform',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default Reveal;
